import * as React from 'react';
import { Icon } from 'office-ui-fabric-react';
import { withRouter } from "react-router-dom";
import { RouteComponentProps } from 'react-router-dom';
import * as ProgramsListStore from '../../../store/ProgramsListStore';
import { ActiveFilter as ProgramActiveFilter } from '../../../store/program/filters';
import * as ProgramsUtils from "../../../store/program/utils";
import { IEntityCardProps } from '../../common/interfaces/IEntity';
import Logo from "../../common/Logo";
import { toPercentString, formatFieldValue } from "../../utils/common";
import { StatusCategory, Dictionary, EntityType, IUserInfo } from "../../../entities/common";
import * as Metadata from "../../../entities/Metadata";
import CardMetric, { CardMetricConfig } from "./CardMetric";
import { ActiveFilter as ProjectActiveFilter } from '../../../store/project/filters';
import EntityWarning from "../../common/warnings/EntityWarning";
import { nameof } from '../../../store/services/metadataService';
import ResourceFormatter from '../../common/formatters/ResourceFormatter';
import PortfolioFormatter from '../../common/formatters/PortfolioFormatter';
import CardCalculationStatuses from './CardCalculationStatuses';
import LinkedSystemIcons from '../../common/sectionsControl/uiControls/summaryControl/LinkedSystemIcons';
import { CostsKPI, ProjectsWorkKPI } from './KPICard';
import CategoryColorStatusView from '../list/columns/CategoryColorStatusView';
import * as StatusDescriptorFactory from '../../../entities/StatusDescriptorFactory';
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { UserState } from '../../../store/User';

export type ProgramCardState = {};
type OwnProps = {
    showWork?: boolean;
    showCosts?: boolean;
    onMenuRender: (entity: ProgramsListStore.Program) => JSX.Element | null;
    fields: Dictionary<Metadata.Field>;
} & IEntityCardProps<ProgramsListStore.Program, ProgramCardState>;

type StateProps = {
    user: UserState,
    projectFields: Metadata.Field[];
};

type Props = OwnProps & StateProps & RouteComponentProps<{}>;

const ProgramCard = (props: Props) => {
    const { entity, showCosts, showWork, fields } = props;
    const attrs = entity.attributes;
    const stage = attrs.Stage;
    const overallStatusField = fields[nameof<ProgramsListStore.ProgramAttrs>("OverallStatus")];
    const overallStatusDescriptor = StatusDescriptorFactory.createStatusDescriptor(overallStatusField);
    const statusOption = overallStatusDescriptor.getOptionOrDefault(attrs.OverallStatus, StatusCategory.NA);

    const goToProjects = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        props.history.push(`/projects`, new ProjectActiveFilter("Custom").withProgram(props.entity.id).buildLocationState());
    }

    const goToPrograms = (e: any, resource: IUserInfo) => {
        e.stopPropagation();
        e.preventDefault();
        props.history.push(`/programs`, new ProgramActiveFilter("Custom").withManager(resource).build());
    }

    const renderMetrics = (): JSX.Element[] => {
        const stageConfig = ProgramsListStore.programStagesMap[entity.attributes.Stage];
        const { completed, total } = ProgramsUtils.getProjectsMetrics(entity.calculation);
        let progressValue: string | undefined;
        if (total) {
            progressValue = `${completed}/${total}`;
        }

        const metrics: { config?: CardMetricConfig, key: string, renderItem?: () => JSX.Element | null, className?: string }[] = [
            {
                config: {
                    title: "Budget",
                    value: formatFieldValue(entity.attributes.Budget, Metadata.FormatType.Cost, props.user)
                },
                key: "budget"
            },
            {
                config: {
                    title: "Progress",
                    value: progressValue || '-'
                },
                key: "progress"
            },
            {
                config: {
                    title: "Stage",
                    value: stageConfig.title,
                    valueClassName: `colorfull ${stageConfig.cssClassName}`
                },
                key: "stage"
            },
            {
                config: {
                    title: "ID",
                    value: entity.attributes.Identifier
                },
                key: "id"
            }
        ];

        if (props.showCosts) {
            const { actual, plan } = entity.calculation.costs;
            metrics.splice(0,
                0,
                {
                    config: {
                        title: "Cost Completed",
                        value: plan ? toPercentString(actual / plan) : "N/A" 
                    },
                    key: "cost-completed"
                });
        } else {
            const ratio = completed / total;
            metrics.splice(0,
                0,
                {
                    config: {
                        title: "Work Completed",
                        value: toPercentString(ratio)
                    },
                    key: "work-completed"
                });
        }
        return metrics.map(_ => <CardMetric config={_.config} renderItem={_.renderItem} className={_.className} key={_.key} />);
    }

    return (
        <div className="card flex-card pf-card" onClick={(e) => {
            e.ctrlKey ? window.open(`/program/${entity.id}`, '_blank') : props.history.push(`/program/${entity.id}`);
        }}>
            <div className={"content" + ([ProgramsListStore.ProgramStage.Closed, ProgramsListStore.ProgramStage.Inactive].indexOf(stage) !== -1 ? " closed" : "")}>
                <div className="first-row">
                    <div style={{ "float": "left" }}>
                        <Logo imageId={entity.imageId} className="prog-logo" />
                    </div>
                    <div className="info">
                        <div className="title overflow-text" title={attrs.Name}>{attrs.Name}</div>
                        <div className="primary-details">
                            <CategoryColorStatusView
                                statusOption={statusOption}
                                trend={entity.insights.statuses[nameof<ProgramsListStore.ProgramAttrs>("OverallStatus")]?.trend}
                                title={overallStatusField?.label} />
                            <div className="connect-buttons">
                                <LinkedSystemIcons
                                    sourceInfos={entity.sourceInfos}
                                    excludeCollaborative
                                    showTitle
                                    allowNavigate
                                    entityType={EntityType.Program} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="metrics">
                    {
                        renderMetrics()
                    }
                </div>
                <div className="separator"></div>

                {showCosts && <CostsKPI costs={entity.calculation.costs} />}
                {showWork && <ProjectsWorkKPI {...ProgramsUtils.getProjectsMetrics(entity.calculation)} />}

                <div className="row">
                    <div className="align-center">
                        <Icon iconName="PPMXPortfolio" className="card-icon" />
                        <span className="title">Portfolio</span>
                    </div>
                    {
                        attrs.Portfolio.length > 0
                            ? <div className="align-center">
                                <PortfolioFormatter portfolio={attrs.Portfolio} withNavigation={true} />
                            </div>
                            : <div className="align-center">
                                <span>(Program is not within any portfolio)</span>
                            </div>
                    }
                </div>

                <div className="row">
                    <div className="align-center link" onClick={goToProjects}>
                        <Icon iconName="PPMXProject" className="card-icon" />
                        <span className="title">Projects</span>
                    </div>
                    {Object.keys(entity.calculation.projectStatuses).length === 0
                        ? <div className="align-center">
                            <span>(No projects included yet)</span>
                        </div>
                        : <div className="align-center">
                            <CardCalculationStatuses
                                statuses={entity.calculation.projectStatuses}
                                goTo={(category: StatusCategory) => {
                                    const state = new ProjectActiveFilter("Custom")
                                        .withProgram(entity.id).withStatusesOfCategory(category, props.projectFields).buildLocationState();
                                    props.history.push(`/projects`, state);
                                }} />
                        </div>}
                </div>

                <div className="row">
                    <div className="align-center">
                        <Icon iconName="PPMXCardManager" className="card-icon" />
                        <span className="title">Manager</span>
                    </div>
                    {
                        attrs.Manager && attrs.Manager.length > 0
                            ? <div className="align-center">
                                <ResourceFormatter resource={attrs.Manager} onClick={goToPrograms} />
                            </div>
                            : <div className="align-center">
                                <span>(Manager is not assigned yet)</span>
                            </div>
                    }
                </div>
                <div className="separator"></div>
                <div className="actions">
                    {props.onMenuRender(entity)}
                    <EntityWarning
                        warnings={entity.warnings}
                        multipleItemsMessage="There are warnings related to this program. Please review program details for more information"
                    />
                </div>
            </div>
        </div>
    );
}


function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    const projectFields = state.fields[EntityType.Project];

    return {
        user: state.user,
        projectFields: projectFields.allIds.map(_ => projectFields.byId[_])
    };
}

export default withRouter<OwnProps>(connect(mapStateToProps)(ProgramCard));
